import React, { useState, useMemo } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// MOISTURE & BASIS CALCULATOR
// Coal / Biomass Proximate Analysis — Multi-Stage Moisture
// Stages: As-Received (Tear) → Air-Dry → Final (As-Determined)
// Bases:  AR | AD | DB | DAF | AFDB
// ─────────────────────────────────────────────────────────────────────────────

const fmt4 = (n) => (n === null || n === undefined || isNaN(n)) ? '—' : parseFloat(n.toFixed(4));
const fmt2 = (n) => (n === null || n === undefined || isNaN(n)) ? '—' : parseFloat(n.toFixed(2));
const pct  = (n) => typeof n === 'number' && !isNaN(n) ? `${fmt4(n)}%` : '—';

// ── Core basis conversion formulas ────────────────────────────────────────────
// M_AR  = as-received (total) moisture %
// M_AD  = air-dry (residual) moisture %
// Ash   = ash % (as-received)
// AR value → other bases
function toAD(ar, M_AR, M_AD) {
  // AD = AR × (100 - M_AD) / (100 - M_AR)
  if (M_AR >= 100) return null;
  return ar * (100 - M_AD) / (100 - M_AR);
}
function toDB(ar, M_AR) {
  if (M_AR >= 100) return null;
  return ar * 100 / (100 - M_AR);
}
function toDAF(ar, M_AR, Ash_AR) {
  const denom = 100 - M_AR - Ash_AR;
  if (denom <= 0) return null;
  return ar * 100 / denom;
}
function toAFDB(ar, M_AR, Ash_AR) {
  // Same as DAF for proximate analysis
  return toDAF(ar, M_AR, Ash_AR);
}

// Total moisture from two-stage
// M_total = M_AR_field + M_AD × (100 - M_AR_field) / 100
function calcTotalMoisture(M_field, M_AD) {
  const mf = parseFloat(M_field);
  const ma = parseFloat(M_AD);
  if (isNaN(mf) || isNaN(ma)) return null;
  return mf + ma * (100 - mf) / 100;
}

// Gross CV (MJ/kg or BTU/lb) — Seyler / Dulong approximation (optional)
// We let user enter measured values; we just do basis conversions

const INPUT_STYLE = {
  width: '100%', border: '1px solid #cdd3e4', borderRadius: 3,
  padding: '4px 7px', fontSize: 12, outline: 'none',
  textAlign: 'right', background: 'white',
};

const CELL = { padding: '5px 8px', fontSize: 11, borderBottom: '1px solid #eef0f8' };
const HEAD = { ...CELL, background: '#1e3060', color: 'white', fontWeight: 700, fontSize: 10 };
const SUB  = { ...HEAD, background: '#2a5080', fontSize: 10 };

function ResultCell({ val, highlight }) {
  const v = fmt4(val);
  return (
    <td style={{ ...CELL, textAlign: 'right', fontWeight: 600,
      background: highlight ? '#f0f6ff' : v === '—' ? '#fafafa' : 'white',
      color: v === '—' ? '#ccc' : highlight ? '#1e3060' : '#222' }}>
      {v === '—' ? '—' : v}
    </td>
  );
}

// ── Parameter rows config ─────────────────────────────────────────────────────
const PARAM_GROUPS = [
  {
    group: 'Proximate Analysis',
    params: [
      { key: 'moisture_ad', label: 'Moisture (Air-Dry / Residual)', unit: '%', skipDB: true },
      { key: 'ash',         label: 'Ash',                           unit: '%' },
      { key: 'volatile',    label: 'Volatile Matter',               unit: '%' },
      { key: 'fixed_carbon',label: 'Fixed Carbon',                  unit: '%', auto: true,
        hint: 'Auto: 100 − Moisture(AD) − Ash − VM' },
    ]
  },
  {
    group: 'Calorific Value',
    params: [
      { key: 'gcv',  label: 'Gross Calorific Value (GCV / HHV)', unit: 'BTU/lb or MJ/kg' },
      { key: 'ncv',  label: 'Net Calorific Value (NCV / LHV)',   unit: 'BTU/lb or MJ/kg' },
    ]
  },
  {
    group: 'Ultimate Analysis',
    params: [
      { key: 'carbon',   label: 'Carbon (C)',   unit: '%' },
      { key: 'hydrogen', label: 'Hydrogen (H)', unit: '%' },
      { key: 'nitrogen', label: 'Nitrogen (N)', unit: '%' },
      { key: 'sulfur',   label: 'Sulfur (S)',   unit: '%' },
      { key: 'oxygen',   label: 'Oxygen (O)',   unit: '%', auto: true,
        hint: 'Auto: 100 − C − H − N − S − Ash − Moisture' },
    ]
  },
  {
    group: 'Other',
    params: [
      { key: 'chlorine',  label: 'Chlorine',   unit: '%' },
      { key: 'fluorine',  label: 'Fluorine',   unit: 'mg/kg' },
    ]
  }
];

export default function MoistureCalculator({ onClose, onApplyToResults }) {
  // ── Moisture inputs ──────────────────────────────────────────────────────
  const [M_field, setM_field] = useState('');   // As-received / tear moisture %
  const [M_AD,    setM_AD]    = useState('');   // Air-dry (residual) moisture %
  const [Ash_AR,  setAsh_AR]  = useState('');   // Ash % as-received (for DAF)

  // Parameter values — all entered on AIR-DRY basis (as-determined from lab)
  const [vals, setVals] = useState({});
  const setVal = (k, v) => setVals(p => ({ ...p, [k]: v }));

  // ── Derived moisture values ──────────────────────────────────────────────
  const mf   = parseFloat(M_field);
  const mad  = parseFloat(M_AD);
  const ashAR = parseFloat(Ash_AR);

  const M_total = useMemo(() => calcTotalMoisture(M_field, M_AD), [M_field, M_AD]);
  const M_AR    = M_total !== null ? M_total : (!isNaN(mf) ? mf : NaN);
  const M_AD_v  = !isNaN(mad) ? mad : 0;

  // Fixed carbon (auto)
  const ash_v     = parseFloat(vals.ash || '');
  const vol_v     = parseFloat(vals.volatile || '');
  const fc_auto   = (!isNaN(ash_v) && !isNaN(vol_v))
    ? 100 - M_AD_v - ash_v - vol_v : null;

  // Oxygen (auto) — ultimate
  const c_v  = parseFloat(vals.carbon   || '');
  const h_v  = parseFloat(vals.hydrogen || '');
  const n_v  = parseFloat(vals.nitrogen || '');
  const s_v  = parseFloat(vals.sulfur   || '');
  const cl_v = parseFloat(vals.chlorine || '');
  const o_auto = (!isNaN(c_v) && !isNaN(h_v) && !isNaN(n_v) && !isNaN(s_v) && !isNaN(ash_v))
    ? 100 - c_v - h_v - n_v - s_v - (ash_v||0) - M_AD_v : null;

  // ── Compute basis table for a given parameter ────────────────────────────
  function computeBases(key, adValue) {
    const ad = parseFloat(adValue);
    if (isNaN(ad) || isNaN(M_AR)) return {};
    const ar    = toAD(ad, M_AD_v, 0) ?? ad;   // AD→AR (reverse: AR = AD × (100-M_AR)/(100-M_AD))
    // Actually: value is entered as AD. Convert to AR first.
    // AD value: measured on air-dried sample
    // AR = AD_val × (100 - M_AR) / (100 - M_AD)
    const ar2   = isNaN(mad) ? ad : ad * (100 - M_AR) / (100 - M_AD_v);
    const db    = toDB(ar2, M_AR);
    const daf   = toDAF(ar2, M_AR, isNaN(ashAR) ? 0 : ashAR);
    return { ad, ar: ar2, db, daf };
  }

  // Get value for a param (use auto if applicable)
  function getVal(param) {
    if (param.auto) {
      if (param.key === 'fixed_carbon') return fc_auto !== null ? String(fmt4(fc_auto)) : '';
      if (param.key === 'oxygen')       return o_auto  !== null ? String(fmt4(o_auto))  : '';
    }
    return vals[param.key] || '';
  }

  // ── Build the results for Apply to Results ───────────────────────────────
  const buildResults = () => {
    const out = {};
    PARAM_GROUPS.forEach(g => g.params.forEach(param => {
      const raw = getVal(param);
      if (raw === '') return;
      const bases = computeBases(param.key, raw);
      out[param.label] = {
        ad: fmt4(bases.ad), ar: fmt4(bases.ar),
        db: fmt4(bases.db), daf: fmt4(bases.daf),
        unit: param.unit,
      };
    }));
    // Add moisture entries
    if (M_field !== '') out['Moisture, % (As-Received/Field)'] = { ar: fmt4(mf), unit: '%' };
    if (M_AD    !== '') out['Moisture, % (Air-Dry/Residual)']  = { ad: fmt4(mad), unit: '%' };
    if (M_total !== null) out['Total Moisture, %'] = { ar: fmt4(M_total), unit: '%' };
    return out;
  };

  const canApply = onApplyToResults && (M_field !== '' || M_AD !== '');

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12 }}>

      {/* ── Moisture inputs ── */}
      <div style={{ background: '#e8f4ff', border: '1px solid #b0d0f0', borderRadius: 6,
        padding: '12px 16px', marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: '#1e3060', marginBottom: 10 }}>
          💧 Moisture Inputs
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#555',
              textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
              As-Received / Tear Moisture %
            </label>
            <input value={M_field} onChange={e => setM_field(e.target.value)}
              placeholder="e.g. 25.0"
              style={{ ...INPUT_STYLE, borderColor: '#1a7dc4', borderWidth: 2 }} />
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              Field / gross moisture
            </div>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#555',
              textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
              Air-Dry / Residual Moisture %
            </label>
            <input value={M_AD} onChange={e => setM_AD(e.target.value)}
              placeholder="e.g. 8.5"
              style={{ ...INPUT_STYLE, borderColor: '#9c27b0', borderWidth: 2 }} />
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              Lab / residual moisture
            </div>
          </div>
          <div>
            <label style={{ fontSize: 10, fontWeight: 700, color: '#555',
              textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
              Ash % (As-Received)
            </label>
            <input value={Ash_AR} onChange={e => setAsh_AR(e.target.value)}
              placeholder="e.g. 5.2"
              style={{ ...INPUT_STYLE, borderColor: '#e09900', borderWidth: 2 }} />
            <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
              Required for DAF basis
            </div>
          </div>
        </div>

        {/* Moisture summary */}
        {(M_field !== '' || M_AD !== '') && (
          <div style={{ display: 'flex', gap: 20, marginTop: 12, padding: '8px 12px',
            background: 'white', borderRadius: 4, border: '1px solid #b0d0f0',
            flexWrap: 'wrap' }}>
            <div>
              <span style={{ fontSize: 10, color: '#888' }}>Tear / Field Moisture:</span>
              <strong style={{ marginLeft: 6, color: '#1a7dc4' }}>{pct(mf)}</strong>
            </div>
            <div>
              <span style={{ fontSize: 10, color: '#888' }}>Air-Dry Moisture:</span>
              <strong style={{ marginLeft: 6, color: '#9c27b0' }}>{pct(mad)}</strong>
            </div>
            {M_total !== null && (
              <div style={{ padding: '2px 10px', background: '#1e3060', borderRadius: 4 }}>
                <span style={{ fontSize: 10, color: '#aac4e0' }}>Total Moisture =</span>
                <strong style={{ marginLeft: 6, color: 'white', fontSize: 13 }}>{pct(M_total)}</strong>
              </div>
            )}
            {!isNaN(ashAR) && (
              <div>
                <span style={{ fontSize: 10, color: '#888' }}>Ash (AR):</span>
                <strong style={{ marginLeft: 6, color: '#e09900' }}>{pct(ashAR)}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Parameter entry + basis table ── */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ ...HEAD, textAlign: 'left', width: '28%' }}>Parameter</th>
              <th style={{ ...SUB, textAlign: 'center', width: '11%' }}>
                Enter Value<br />
                <span style={{ fontSize: 9, fontWeight: 400, color: '#aac4e0' }}>(Air-Dry basis)</span>
              </th>
              <th style={{ ...HEAD, textAlign: 'center', width: '9%', background: '#1a7dc4' }}>
                As-Received<br />(AR)
              </th>
              <th style={{ ...HEAD, textAlign: 'center', width: '9%', background: '#2a5080' }}>
                Air-Dry<br />(AD)
              </th>
              <th style={{ ...HEAD, textAlign: 'center', width: '9%', background: '#1e3060' }}>
                Dry Basis<br />(DB)
              </th>
              <th style={{ ...HEAD, textAlign: 'center', width: '9%', background: '#1a4060' }}>
                Dry Ash-Free<br />(DAF / AFDB)
              </th>
              <th style={{ ...SUB, textAlign: 'center', width: '8%' }}>Unit</th>
            </tr>
          </thead>
          <tbody>
            {PARAM_GROUPS.map(group => (
              <React.Fragment key={group.group}>
                {/* Group header */}
                <tr>
                  <td colSpan={7} style={{ background: '#f0f4ff', fontWeight: 700,
                    fontSize: 11, color: '#1e3060', padding: '5px 8px',
                    borderBottom: '1px solid #dde4f8' }}>
                    {group.group}
                  </td>
                </tr>
                {group.params.map(param => {
                  const raw = getVal(param);
                  const isAuto = param.auto && raw !== '';
                  const bases = raw !== '' ? computeBases(param.key, raw) : {};
                  return (
                    <tr key={param.key} style={{ background: isAuto ? '#fffbee' : 'white' }}>
                      <td style={{ ...CELL, color: '#333' }}>
                        {param.label}
                        {isAuto && (
                          <span style={{ fontSize: 9, marginLeft: 6, color: '#e09900',
                            fontStyle: 'italic' }}>auto</span>
                        )}
                        {param.hint && (
                          <div style={{ fontSize: 9, color: '#aaa', marginTop: 1 }}>
                            {param.hint}
                          </div>
                        )}
                      </td>
                      <td style={{ ...CELL, padding: '3px 6px' }}>
                        {param.auto ? (
                          <div style={{ textAlign: 'right', fontWeight: 600,
                            color: raw !== '' ? '#e09900' : '#ccc', padding: '4px 7px' }}>
                            {raw !== '' ? raw : '—'}
                          </div>
                        ) : (
                          <input
                            value={vals[param.key] || ''}
                            onChange={e => setVal(param.key, e.target.value)}
                            placeholder="enter"
                            style={INPUT_STYLE} />
                        )}
                      </td>
                      <ResultCell val={bases.ar} highlight />
                      <ResultCell val={bases.ad} />
                      <ResultCell val={bases.db} highlight />
                      <ResultCell val={bases.daf} highlight />
                      <td style={{ ...CELL, color: '#888', fontSize: 10 }}>{param.unit}</td>
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Basis legend ── */}
      <div style={{ display: 'flex', gap: 16, marginTop: 12, padding: '8px 12px',
        background: '#f8f9ff', borderRadius: 4, fontSize: 10, flexWrap: 'wrap',
        border: '1px solid #e0e4ec' }}>
        <strong style={{ color: '#1e3060' }}>Conversion Formulas:</strong>
        <span><strong>AR</strong> = AD × (100−M<sub>total</sub>) / (100−M<sub>AD</sub>)</span>
        <span><strong>DB</strong> = AR × 100 / (100−M<sub>total</sub>)</span>
        <span><strong>DAF/AFDB</strong> = AR × 100 / (100−M<sub>total</sub>−Ash<sub>AR</sub>)</span>
        <span style={{ color: '#888' }}>M<sub>total</sub> = M<sub>field</sub> + M<sub>AD</sub> × (100−M<sub>field</sub>) / 100</span>
      </div>

      {/* ── Buttons ── */}
      {(canApply || onClose) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
          {canApply && (
            <button
              onClick={() => onApplyToResults && onApplyToResults(buildResults())}
              style={{ background: '#1a6b42', color: 'white', border: 'none',
                borderRadius: 4, padding: '7px 18px', cursor: 'pointer',
                fontSize: 12, fontWeight: 700 }}>
              ✅ Apply Calculated Values to Results
            </button>
          )}
          {onClose && (
            <button onClick={onClose}
              style={{ background: '#f0f2f8', color: '#333', border: '1px solid #cdd3e4',
                borderRadius: 4, padding: '7px 16px', cursor: 'pointer', fontSize: 12 }}>
              Close
            </button>
          )}
        </div>
      )}
    </div>
  );
}
